import { useEffect, useMemo, useRef } from 'react';

import useAccountQuery from 'api/queries/useAccountQuery';
import useSubscriptionQuery from 'api/queries/useSubscriptionQuery';
import { FEATURE_FLAG_NAMES, useAmplitudeFeatureFlag } from 'hooks/useFeatureFlag';
import useGlobalStore from 'hooks/useGlobalStore';
import useStatusParam from 'hooks/useStatusParam';

const ONBOARDING_OPEN_WINDOW_SHOWN_KEY = 'ONBOARDING_OPEN_WINDOW_SHOWN';
const ONBOARDING_CLOSED_WINDOW_SHOWN_KEY = 'ONBOARDING_CLOSED_WINDOW_SHOWN';

const MAX_DAYS_SINCE_SIGNUP = 14;
const MAX_ORDERS_FOR_ONBOARDING = 2;

function markOpenWindowOnboardingShown() {
    localStorage.setItem(ONBOARDING_OPEN_WINDOW_SHOWN_KEY, 'true');
}

function markClosedWindowOnboardingShown() {
    localStorage.setItem(ONBOARDING_CLOSED_WINDOW_SHOWN_KEY, 'true');
}

function hasOpenWindowOnboardingBeenShown() {
    return localStorage.getItem(ONBOARDING_OPEN_WINDOW_SHOWN_KEY) === 'true';
}

function hasClosedWindowOnboardingBeenShown() {
    return localStorage.getItem(ONBOARDING_CLOSED_WINDOW_SHOWN_KEY) === 'true';
}

/**
 * Custom hook to manage the display logic for onboarding modals.
 *
 * This hook determines whether to show closed or open window onboarding modals
 * based on account information, subscription data, and local storage. It provides
 * callback handlers to mark when onboarding modals have been shown and to open/close these modals.
 *
 * @param {Object} params
 * @param {Function} params.setClosedWindowOnboardingModalOpen - Setter to control the visibility of the closed window onboarding modal.
 * @param {Function} params.setOpenWindowOnboardingModalOpen - Setter to control the visibility of the open window onboarding modal.
 *
 * @returns {object}
 */
function useOnboardingModals({ setClosedWindowOnboardingModalOpen, setOpenWindowOnboardingModalOpen }) {
    const { isMisfitsTheme } = useGlobalStore();
    const { data: account } = useAccountQuery();
    const { data: subscription } = useSubscriptionQuery();
    const { isMembershipSuccess } = useStatusParam();

    const { variantAssignment: onboardingOptimizationsVariant } = useAmplitudeFeatureFlag({
        flagName: FEATURE_FLAG_NAMES.ONBOARDING_OPTIMIZATIONS,
    });
    const isOnboardingOptimizationEnabled =
        onboardingOptimizationsVariant === 'on' && isMisfitsTheme && !window.isNativeApp;

    const marketplaceOpen = account?.marketplaceOpen;
    const numOrders = account?.numOrders;
    const signupDate = subscription?.createdAt?.date;

    const isEligibleForOnboarding = useMemo(() => {
        if (numOrders === undefined || !signupDate) {
            return false;
        }

        if (numOrders >= MAX_ORDERS_FOR_ONBOARDING) {
            return false;
        }

        const signupDateTime = new Date(signupDate).getTime();
        const now = Date.now();
        const daysSinceSignup = (now - signupDateTime) / (1000 * 60 * 60 * 24);

        return daysSinceSignup <= MAX_DAYS_SINCE_SIGNUP;
    }, [numOrders, signupDate]);

    const previousMarketplaceOpen = useRef(undefined);

    const closeOpenWindowModal = () => {
        setOpenWindowOnboardingModalOpen(false);
        markOpenWindowOnboardingShown();
    };

    const closeClosedWindowModal = () => {
        setClosedWindowOnboardingModalOpen(false);
        markClosedWindowOnboardingShown();
    };

    const triggerOpenWindowOnboarding = () => {
        if (!hasOpenWindowOnboardingBeenShown()) {
            setOpenWindowOnboardingModalOpen(true);
        }
    };

    const triggerClosedWindowOnboarding = () => {
        if (!hasClosedWindowOnboardingBeenShown()) {
            setClosedWindowOnboardingModalOpen(true);
        }
    };

    useEffect(() => {
        const hasDataLoaded = numOrders !== undefined && signupDate;
        if (hasDataLoaded && !isEligibleForOnboarding) {
            localStorage.removeItem(ONBOARDING_OPEN_WINDOW_SHOWN_KEY);
            localStorage.removeItem(ONBOARDING_CLOSED_WINDOW_SHOWN_KEY);
        }
    }, [isEligibleForOnboarding, numOrders, signupDate]);

    useEffect(() => {
        if (!isOnboardingOptimizationEnabled || marketplaceOpen === undefined) return;

        const isInitialLoad = previousMarketplaceOpen.current === undefined;
        const wasOpen = previousMarketplaceOpen.current === true;
        const wasClosed = previousMarketplaceOpen.current === false;

        // Update ref before checking isMembershipSuccess so we track marketplace state
        // even when returning early. This prevents showing the modal when
        // isMembershipSuccess later becomes false.
        previousMarketplaceOpen.current = marketplaceOpen;

        if (isMembershipSuccess) return;

        if (isEligibleForOnboarding && marketplaceOpen) {
            if ((isInitialLoad || wasClosed) && !hasOpenWindowOnboardingBeenShown()) {
                setOpenWindowOnboardingModalOpen(true);
            }
        } else if (isEligibleForOnboarding && !marketplaceOpen) {
            if ((isInitialLoad || wasOpen) && !hasClosedWindowOnboardingBeenShown()) {
                setClosedWindowOnboardingModalOpen(true);
            }
        }
    }, [
        isMembershipSuccess,
        isOnboardingOptimizationEnabled,
        marketplaceOpen,
        isEligibleForOnboarding,
        setClosedWindowOnboardingModalOpen,
        setOpenWindowOnboardingModalOpen,
    ]);

    return {
        closeClosedWindowModal,
        closeOpenWindowModal,
        hasClosedWindowOnboardingBeenShown,
        hasOpenWindowOnboardingBeenShown,
        isEligibleForOnboarding,
        isOnboardingOptimizationEnabled,
        markClosedWindowOnboardingShown,
        markOpenWindowOnboardingShown,
        triggerClosedWindowOnboarding,
        triggerOpenWindowOnboarding,
    };
}

export default useOnboardingModals;

export {
    ONBOARDING_CLOSED_WINDOW_SHOWN_KEY,
    MAX_DAYS_SINCE_SIGNUP,
    MAX_ORDERS_FOR_ONBOARDING,
    ONBOARDING_OPEN_WINDOW_SHOWN_KEY,
};
